import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type ChartAccountTree,
  type AccountType,
  ACCOUNT_TYPE_LABELS,
} from "@/hooks/useChartOfAccounts";
import { ChevronRight, Lock, MoreHorizontal, Pencil, Power } from "lucide-react";
import { cn } from "@/lib/utils";

interface AccountTreeProps {
  accounts: ChartAccountTree[];
  onEdit: (account: ChartAccountTree) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
}

const TYPE_COLORS: Record<AccountType, string> = {
  revenue: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  deduction: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
  variable_cost: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  fixed_expense: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  financial_expense: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  other: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
};

export function AccountTree({ accounts, onEdit, onToggleActive }: AccountTreeProps) {
  if (accounts.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Nenhuma conta cadastrada. Clique em "Inicializar Plano de Contas" para criar as contas padrão.
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">Código</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead className="w-[150px]">Tipo</TableHead>
            <TableHead className="w-[100px]">Status</TableHead>
            <TableHead className="w-[60px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {accounts.map((account) => (
            <TableRow
              key={account.id}
              className={cn(
                !account.is_active && "opacity-50"
              )}
            >
              <TableCell className="font-mono text-sm">
                <div
                  className="flex items-center gap-1"
                  style={{ paddingLeft: `${account.level * 1.5}rem` }}
                >
                  {account.children.length > 0 && (
                    <ChevronRight className="h-3 w-3 text-muted-foreground" />
                  )}
                  {account.code}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <span
                    style={{ paddingLeft: `${account.level * 1.5}rem` }}
                    className={cn(
                      account.level === 0 && "font-semibold"
                    )}
                  >
                    {account.name}
                  </span>
                  {account.is_system && (
                    <Lock className="h-3 w-3 text-muted-foreground" />
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Badge
                  variant="secondary"
                  className={cn("text-xs", TYPE_COLORS[account.account_type])}
                >
                  {ACCOUNT_TYPE_LABELS[account.account_type]}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge variant={account.is_active ? "default" : "secondary"}>
                  {account.is_active ? "Ativa" : "Inativa"}
                </Badge>
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => onEdit(account)}
                      disabled={account.is_system}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onToggleActive(account.id, !account.is_active)}
                      disabled={account.is_system}
                    >
                      <Power className="mr-2 h-4 w-4" />
                      {account.is_active ? "Desativar" : "Ativar"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
